import React, { Fragment } from "react";
import useSWR from "swr";
import { Text, Toaster, useToaster } from "@vagastec/ensemble-core";
import { useHistory } from "react-router-dom";
import { fetcherWithToken } from "./fetcher-with-token";
import { renderSelectOptions } from "./render-select-options";
import { FormCard } from "./components/form-card";
import { Select } from "./components/select";
import { stepStyle } from "./step.css";
import { Label } from "./components/label";
import { Fieldset } from "./components/fieldset";
import { Space } from "./components/space";
import { RadioButton } from "./components/radio-button";
import { Input } from "./components/input";
import { Button } from "./components/button";
import {
  primaryButton,
  secondaryButton,
  inputVariants,
} from "./components/button.css";
import { formButtonsWrapper } from "./index.css";
import { Switch } from "./components/switch";
import {
  vagaModeloTextStyle,
  lockStyle,
  selectedBenefits,
  infoIconContainer,
} from "./step-1.css";
import { TextEditor } from "./components/text-editor";
import { IlustracaoPrancheta } from "./illustration/ilustracao-prancheta";
import { InfoBox } from "./components/info-box";
import { useDebounce } from "./debounce";
import { useLocation } from "react-router-dom";
import { IconeLockAlt } from "./icons/icone-lock-alt";
import { Combobox } from "./components/combobox";
import { FieldsetSobreEmpresa } from "./fieldset-sobre-empresa";
import { extractTextFromHtmlString } from "./extract-text-from-html-string";
import FormCardLoader from "./components/form-card-loader";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { Multi } from "./components/multi";
import { vars } from "./theme.css";
import { benefitItemPreview, separatorStyle } from "./step-4.css";
import { IconesBeneficios } from "./icons/icones-beneficios";
import { ControlePosicoes } from "./controle-posicoes";
import { Salario } from "./salario";
import { formatString } from "./utils/formatString";
import { Alert } from "./components/alert";
import { InfoIcon } from "./icons/info-icon";

export function Step1({ formState, setFormState, setSelectedStepId }) {
  const lastSugestoesCargoQuery = useDebounce(formState.cargo, 500);
  const [addressFieldNode, setAddressFieldNode] = React.useState();
  const [hasTriedToSubmit, setHasTriedToSubmit] = React.useState(false);
  const location = useLocation();
  const isVagaModelo = location.pathname === "/nova-vaga-modelo";

  const funcionarioId = formState.painelData?.funcionario.id;

  const intl = useIntl();

  let history = useHistory();
  const [toasts, addToast, removeToast] = useToaster();
  const descricaoTextLength = extractTextFromHtmlString(
    formState.descricao
  ).length;

  const outrosRequisitosTextLength = extractTextFromHtmlString(
    formState.outrosRequisitos
  ).length;

  const addressFieldRef = React.useCallback((node) => {
    if (node) setAddressFieldNode(node);
  }, []);

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  React.useEffect(() => {
    if (window.google && addressFieldNode) {
      const autocomplete = new google.maps.places.Autocomplete(
        addressFieldNode,
        {}
      );

      autocomplete.addListener("place_changed", () => {
        setFormState({
          enderecoQuery: autocomplete.getPlace()?.formatted_address,
          enderecoFormatado: autocomplete.getPlace()?.formatted_address,
        });
        const components = autocomplete.getPlace()?.address_components;
        setFormState({
          enderecoLat: autocomplete.getPlace()?.geometry.location.lat(),
          enderecoLng: autocomplete.getPlace()?.geometry.location.lng(),
        });
        if (components) {
          setFormState({
            enderecoApiPais:
              components.find((v) => v.types.includes("country"))?.long_name ??
              "",
            enderecoApiEstado:
              components.find((v) =>
                v.types.includes("administrative_area_level_1")
              )?.short_name ?? "",
            enderecoApiCidade:
              components.find((v) =>
                v.types.includes("administrative_area_level_2")
              )?.short_name ?? "",
            enderecoApiBairro:
              components.find((v) => v.types.includes("sublocality_level_1"))
                ?.long_name ?? "",
            enderecoApiRua:
              components.find((v) => v.types.includes("route"))?.long_name ??
              "",
            enderecoApiNumero:
              components.find((v) => v.types.includes("street_number"))
                ?.long_name ?? "",
          });

          if (
            !components.find((v) =>
              v.types.includes("administrative_area_level_1")
            )
          ) {
            return setFormState({ enderecoRestricao: "somente_pais" });
          }

          if (
            !components.find((v) =>
              v.types.includes("administrative_area_level_2")
            )
          ) {
            return setFormState({ enderecoRestricao: "somente_estado" });
          }

          return setFormState({ enderecoRestricao: "somente_cidade" });
        }
      });
    }
  }, [addressFieldNode]);

  const { data: sugestoesCargoData, error: sugestoesCargoError } = useSWR(
    () =>
      lastSugestoesCargoQuery?.length >= 3
        ? `${process.env.HESTIA}/sugestoes/cargos?q=${lastSugestoesCargoQuery}&funcionario_id=${funcionarioId}`
        : null,
    fetcherWithToken
  );

  const [beneficiosCategorias, setBeneficiosCategorias] = React.useState();

  React.useEffect(() => {
    if (formState.beneficiosData) {
      let categorias = [];

      formState.beneficiosData.map((v) => {
        if (categorias.map((v) => v.id).includes(v.categoria.id)) return;
        categorias.push({ id: v.categoria.id, label: v.categoria.nome });
      });

      categorias = categorias.sort((a, b) => a.id - b.id);

      setBeneficiosCategorias(categorias);
    }
  }, [formState.beneficiosData]);

  // sugestão de mudanças na api de benefícios:

  // get categorias de benefícios
  // [{ id: 1, label: "Alimentação"}, {id: 2, label: "Educação"}]

  // get benefícios
  // [
  //   {
  //     categoria: 1,
  //     ...
  //   },
  // ];

  const ingles = formState.idiomasData?.find((idioma) => idioma.id === 7);
  const espanhol = formState.idiomasData?.find((idioma) => idioma.id === 4);

  const idiomasOrdenados = formState.idiomasData
    ? [
        ingles,
        espanhol,
        ...formState.idiomasData.filter(
          (idioma) => idioma.id !== 7 && idioma.id !== 4
        ),
      ]
    : [];

  function addImageUploadErrorToast() {
    const title = intl.formatMessage(
      defineMessage({
        id: "mensagem.ocorreuErro",
        defaultMessage: "Ocorreu um erro",
      })
    );

    const body = intl.formatMessage(
      defineMessage({
        id: "step1.uploadBodyErroTamanho",
        defaultMessage:
          "Sua imagem está maior que o tamanho máximo permitido (5MB), ou fora dos formatos permitidos (JPG, JPEG e PNG).",
      })
    );
    const variant = "error";
    const onDismiss = (id) => removeToast(id);
    const timer = "max";

    addToast({
      title,
      variant,
      body,
      timer,
      onDismiss,
    });
  }

  function addImageInsertErrorToast() {
    const title = intl.formatMessage(
      defineMessage({
        id: "mensagem.ocorreuErro",
        defaultMessage: "Ocorreu um erro",
      })
    );

    const body = intl.formatMessage(
      defineMessage({
        id: "step1.imagemUltrapassouDimensoes",
        defaultMessage:
          "A imagem não pode ser maior que 2000px de largura e 1000px de altura.",
      })
    );
    const variant = "error";
    const onDismiss = (id) => removeToast(id);
    const timer = "max";

    addToast({
      title,
      variant,
      body,
      timer,
      onDismiss,
    });
  }

  const limiteAreas = 8;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSelectedStepId(2);
      }}
      className={stepStyle}
    >
      {formState.isPresetPending ? (
        <>
          <Space />
          <FormCardLoader />
          <FormCardLoader />
          <FormCardLoader />
        </>
      ) : (
        <>
          {isVagaModelo ? (
            <FormCard
              title={
                <FormattedMessage
                  id="step1.cadastroVagaModelo"
                  defaultMessage="Cadastro de vaga-modelo"
                />
              }
            >
              <div className={vagaModeloTextStyle}>
                <Text size="rg" as="p">
                  <FormattedMessage
                    id="step1.infoVagaModelo"
                    defaultMessage="Na <strong>etapa 4</strong> utilize o cadeado {icon} para bloquear a edição de informações."
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                      icon: (
                        <IconeLockAlt
                          width={16}
                          height={16}
                          className={lockStyle}
                        />
                      ),
                    }}
                  />
                </Text>
              </div>
            </FormCard>
          ) : null}

          <FormCard
            title={
              <FormattedMessage
                id="step1.sobreCargo"
                defaultMessage="Sobre o cargo"
              />
            }
            data-testid="bloco-cargo"
          >
            <Label
              text={
                <FormattedMessage id="step1.cargo" defaultMessage="Cargo *" />
              }
              data-testid="input-cargo"
              htmlFor="cargo"
            />
            <Combobox
              data={sugestoesCargoData?.data ?? []}
              id="cargo"
              placeholder={intl.formatMessage(
                defineMessage({
                  id: "step1.placeholderCargo",
                  defaultMessage: "Escolha um cargo",
                })
              )}
              error={sugestoesCargoError}
              isLoading={!sugestoesCargoData && !sugestoesCargoError}
              getItemId={(item) => item.id}
              getItemLabel={(item) =>
                `${item.titulo} - ${
                  item.id_legado ? `v${item.id_legado}` : item.id
                } (${
                  item.vaga_modelo
                    ? intl.formatMessage(
                        defineMessage({
                          id: "step1.vagaModelo",
                          defaultMessage: "vaga-modelo",
                        })
                      )
                    : intl.formatMessage(
                        defineMessage({
                          id: "step1.vagaRecorrente",
                          defaultMessage: "vaga recorrente",
                        })
                      )
                })`
              }
              getSelectedItemLabel={(item) => item.titulo}
              query={formState.cargo}
              setQuery={(v) => setFormState({ cargo: v, tempOrigemVaga: {} })}
              onSelectedChange={(e) => {
                setFormState({
                  origemVaga: {},
                  tempOrigemVaga: e,
                  tempVagaId: e.id,
                });
              }}
              data-testid="input-cargo"
              required
              errorMessage={
                <FormattedMessage
                  id="erro.campoObrigatorio"
                  defaultMessage="Campo obrigatório"
                />
              }
              text={
                <FormattedMessage
                  id="step1.vagaAindaNaoCriada"
                  defaultMessage="Você ainda não criou uma vaga com este cargo"
                />
              }
              overlayDataTestId="cargo-overlay"
            />

            <Space height="1rem" />

            <Switch
              label={
                <FormattedMessage
                  id="step1.labelVagaPcd"
                  defaultMessage="Cargo exclusivo para PCD (pessoa com deficiência)"
                />
              }
              checked={formState.isPcd}
              onChange={(e) => setFormState({ isPcd: e.target.checked })}
              data-testid="switch-pcd"
            />

            {formState.tempOrigemVaga?.vaga_modelo && formState.cargo ? (
              <>
                <InfoBox
                  illustration={<IlustracaoPrancheta />}
                  title={
                    <FormattedMessage
                      id="step1.titleUtilizarVagaModelo"
                      defaultMessage="Você escolheu uma vaga-modelo!"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="step1.descriptionUtilizarVagaModelo"
                      defaultMessage="Deseja utilizá-la na abertura do seu processo seletivo?"
                    />
                  }
                  actionContent={
                    <Button
                      className={`${primaryButton} ${inputVariants["form-buttons"]}`}
                      data-testid="btn-usar-vaga"
                      onClick={() => {
                        setSelectedStepId(4);
                        setFormState((prev) => ({
                          vagaId: prev.tempVagaId,
                          origemVaga: prev.tempOrigemVaga,
                        }));
                      }}
                    >
                      <FormattedMessage
                        id="step1.btnUsarVaga"
                        defaultMessage="Usar vaga"
                      />
                    </Button>
                  }
                />
              </>
            ) : null}

            {!formState.tempOrigemVaga?.vaga_modelo &&
            formState.tempOrigemVaga?.id &&
            formState.cargo ? (
              <>
                <InfoBox
                  illustration={<IlustracaoPrancheta />}
                  title={
                    <FormattedMessage
                      id="step1.titleUtilizarVagaRecorrente"
                      defaultMessage="Você já publicou esta vaga!"
                    />
                  }
                  description={
                    <FormattedMessage
                      id="step1.descriptionUtilizarVagaRecorrente"
                      defaultMessage="Deseja utilizá-la na abertura do seu processo seletivo?"
                    />
                  }
                  actionContent={
                    <Button
                      className={`${primaryButton} ${inputVariants["form-buttons"]}`}
                      data-testid="btn-usar-vaga"
                      onClick={() => {
                        setSelectedStepId(4);
                        setFormState((prev) => ({
                          vagaId: prev.tempVagaId,
                          origemVaga: prev.tempOrigemVaga,
                        }));
                      }}
                    >
                      <FormattedMessage
                        id="step1.btnUsarVaga"
                        defaultMessage="Usar vaga"
                      />
                    </Button>
                  }
                />
              </>
            ) : null}

            <Space height="1rem" />

            <Label
              text={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.tipoContratacaoOpcional"
                    defaultMessage={"Tipo de contratação"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.tipoContratacaoObrigatorio"
                    defaultMessage={"Tipo de contratação *"}
                  />
                )
              }
              data-testid="select-tipo-contratacao"
            >
              <Select
                aria-label="tipo de contratação"
                value={formState.modeloContratacaoSelectedId ?? ""}
                onChange={(e) => {
                  setFormState({
                    modeloContratacaoSelectedId: Number(e.target.value),
                  });
                  if (e.target.value === "2")
                    setFormState({ nivelHierarquicoSelectedId: 28 });
                }}
                required={!isVagaModelo}
                shouldValidate={hasTriedToSubmit}
                errorMessage={
                  <FormattedMessage
                    id="erro.campoObrigatorio"
                    defaultMessage="Campo obrigatório"
                  />
                }
                data-testid="select-modelo-contratacao"
              >
                {renderSelectOptions({
                  data: formState.modelosContratacaoData,
                  error: formState.modelosContratacaoError,
                  errorMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.erro",
                      defaultMessage: "Ocorreu um erro",
                    })
                  ),
                  loadingMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.loading",
                      defaultMessage: "Carregando...",
                    })
                  ),
                  selectOptionMessage: intl.formatMessage(
                    defineMessage({
                      id: "select.escolherOpcao",
                      defaultMessage: "Selecione uma opção...",
                    })
                  ),
                })}
              </Select>
            </Label>

            <Space height="1rem" />

            <Label
              text={
                <FormattedMessage
                  id="step1.posicoes"
                  defaultMessage="Número de posições"
                />
              }
              data-testid="posicoes"
            >
              <ControlePosicoes
                formState={formState}
                setFormState={setFormState}
              />
            </Label>

            <Space height="1rem" />

            <Label
              text={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.titleDescricaoOpcional"
                    defaultMessage={"Descrição"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.titleDescricaoObrigatorio"
                    defaultMessage={"Descrição *"}
                  />
                )
              }
              data-testid="bloco-descricao"
              htmlFor="editor-descricao"
            />

            <Space height=".5rem" />
            <Alert>
              <div className={infoIconContainer}>
                <InfoIcon width={20} height={20} />
              </div>
              <Text size="sm" as="p">
                <FormattedMessage
                  id="step1.cargos"
                  defaultMessage="Torne sua descrição mais engajadora com as sugestões da nossa <a></a>."
                  values={{
                    a: (chunks) => (
                      <a
                        href={
                          "https://forbusiness.vagas.com.br/modelos-de-descricao-de-cargo/?utm_source=plataforma&utm[…]ataforma&utm_campaign=clientes-biblioteca&utm_id=produto"
                        }
                        target="_blank"
                        style={{
                          color: vars.color.brand,
                          textDecoration: "none",
                        }}
                      >
                        {chunks}
                        <b>
                          <FormattedMessage
                            id="step1.linkBiblioteca"
                            defaultMessage="biblioteca de cargos"
                          />
                        </b>
                      </a>
                    ),
                  }}
                />
              </Text>
            </Alert>

            <TextEditor
              value={formState.descricao}
              onChange={(e) => setFormState({ descricao: e })}
              length={descricaoTextLength}
              isValid={
                descricaoTextLength + outrosRequisitosTextLength >= 300 ||
                isVagaModelo
              }
              shouldValidate={hasTriedToSubmit}
              placeholder={intl.formatMessage(
                defineMessage({
                  id: "step1.placeholderDescricaoCargo",
                  defaultMessage:
                    "O que a pessoa contratada irá fazer? Como será o dia a dia dessa pessoa?\nQuais são os pré-requisitos técnicos obrigatórios para essa posição?",
                })
              )}
              errorMessage={
                <FormattedMessage
                  id="step1.erroDescricao"
                  defaultMessage="Campo obrigatório. O campo descrição precisa de pelo menos 300 caracteres. Este valor pode ser dividido com o campo Outros Requisitos. :)"
                />
              }
              infoMessage={
                <FormattedMessage
                  id="step1.infoDescricao"
                  defaultMessage="Preencha pelo menos 300 caracteres incluindo o campo Outros Requisitos, se preferir."
                />
              }
              imageUploadUrl={`${process.env.HESTIA}/upload_imagens`}
              onImageUploadError={() => addImageUploadErrorToast()}
              onImageInsertError={() => addImageInsertErrorToast()}
              id="editor-descricao"
              isVariablesHidden
            />

            <Space height="1rem" />

            <Label
              text={
                <FormattedMessage
                  id="step1.labelTextOutrosRequisitos"
                  defaultMessage="Outros requisitos"
                />
              }
              details={
                <FormattedMessage
                  id="step1.labelDetailsOutrosRequisitos"
                  defaultMessage="(soft skills, diferenciais ou requisitos que ainda não foram mencionados)"
                />
              }
              htmlFor="outros-requisitos"
            />
            <TextEditor
              value={formState.outrosRequisitos}
              placeholder={intl.formatMessage(
                defineMessage({
                  id: "step1.placeholderOutrosRequisitos",
                  defaultMessage:
                    "Habilidades interpessoais (exemplo: proatividade, boa comunicação).\nDiferenciais",
                })
              )}
              onChange={(e) => setFormState({ outrosRequisitos: e })}
              id="outros-requisitos"
              isImageHidden
              isVideoHidden
              isVariablesHidden
              length={outrosRequisitosTextLength}
              isValid
            />

            <Label
              text={
                <FormattedMessage
                  id="step1.labelTextIdiomas"
                  defaultMessage="Idiomas"
                />
              }
            >
              <Multi
                name="idiomas"
                options={idiomasOrdenados
                  ?.map((v) =>
                    [
                      {
                        id: 2,
                        label: intl.formatMessage(
                          defineMessage({
                            id: "step1.idiomaBasico",
                            defaultMessage: "Básico",
                          })
                        ),
                      },
                      {
                        id: 3,
                        label: intl.formatMessage(
                          defineMessage({
                            id: "step1.idiomaIntermediario",
                            defaultMessage: "Intermediário",
                          })
                        ),
                      },
                      {
                        id: 4,
                        label: intl.formatMessage(
                          defineMessage({
                            id: "step1.idiomaAvancado",
                            defaultMessage: "Avançado",
                          })
                        ),
                      },
                      {
                        id: 5,
                        label: intl.formatMessage(
                          defineMessage({
                            id: "step1.idiomaFluente",
                            defaultMessage: "Fluente",
                          })
                        ),
                      },
                    ].map((level) => ({
                      ...v,
                      uid: `${v.id}-${level.id}`,
                      levelLabel: level.label,
                      label: `${v.nome} - ${level.label}`,
                      levelId: level.id,
                      value: `${v.id}-${level.id}`,
                    }))
                  )
                  .flat()}
                state={formState.idiomasSelected}
                onChange={(v) => {
                  setFormState({ idiomasSelected: v });
                }}
                noOptionsMessage={() => {
                  return formState.idiomasError ? (
                    <FormattedMessage
                      id="mensagem.ocorreuErro"
                      defaultMessage="Ocorreu um erro"
                    />
                  ) : (
                    <FormattedMessage
                      id="info.carregando"
                      defaultMessage="Carregando..."
                    />
                  );
                }}
                placeholder={
                  <Text size="sm" fontColor={vars.color.lightForeground}>
                    <FormattedMessage
                      id="placeholder.idiomas"
                      defaultMessage="Ex.: inglês avançado, espanhol intermediário"
                    />
                  </Text>
                }
                shouldValidate={hasTriedToSubmit}
              ></Multi>
            </Label>

            <Space height="1rem" />
          </FormCard>

          <FormCard
            title={
              <FormattedMessage
                id="step1.salarioBeneficios"
                defaultMessage="Faixa salarial e benefícios"
              />
            }
            data-testid="bloco-cargo"
          >
            <Salario formState={formState} setFormState={setFormState} />

            <Space height="1rem" />

            <div className={separatorStyle}></div>
            <Space height="1rem" />

            <Label
              text={
                <FormattedMessage
                  id="step1.beneficios"
                  defaultMessage="Adicione benefícios"
                />
              }
            >
              <Multi
                name="beneficios"
                options={beneficiosCategorias?.map((v) => ({
                  ...v,

                  options: formState.beneficiosData?.filter(
                    (b) => b.categoria.id === v.id
                  ),
                }))}
                getOptionValue={(option) => option.id}
                state={formState.beneficiosSelected}
                onChange={(v) => {
                  setFormState({ beneficiosSelected: v });
                }}
                noOptionsMessage={() => {
                  return formState.beneficiosError ? (
                    <FormattedMessage
                      id="mensagem.ocorreuErro"
                      defaultMessage="Ocorreu um erro"
                    />
                  ) : (
                    <FormattedMessage
                      id="info.carregando"
                      defaultMessage="Carregando..."
                    />
                  );
                }}
                placeholder={
                  <Text size="sm" fontColor={vars.color.lightForeground}>
                    <FormattedMessage
                      id="placeholder.pesquiseSelecione"
                      defaultMessage="Pesquise e selecione"
                    />
                  </Text>
                }
                shouldValidate={hasTriedToSubmit}
              ></Multi>

              <div className={selectedBenefits}>
                {formState.beneficiosSelected?.map((v) => (
                  <div className={benefitItemPreview} key={v.id}>
                    <IconesBeneficios
                      aria-hidden="true"
                      dangerouslySetInnerHTML={{
                        __html: v.imagem,
                      }}
                    />
                    <Text size="sm" as="p">
                      {v.label}
                    </Text>

                    {v.permite_valor ? (
                      <Input
                        type="number"
                        placeholder={intl.formatMessage(
                          defineMessage({
                            id: "step1.placeholderValorBeneficio",
                            defaultMessage: "R$ 0,00",
                          })
                        )}
                        variant="inline-value"
                        value={v.value ?? ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            beneficiosSelected: prev.beneficiosSelected.map(
                              (item) =>
                                item.id === v.id
                                  ? { ...item, value: e.target.value }
                                  : item
                            ),
                          }))
                        }
                        data-testid={`valor-${v.id}`}
                        min={1}
                        errorMessage={
                          <FormattedMessage
                            id="step1.valorInválido"
                            defaultMessage="Valor inválido"
                          />
                        }
                        format="currency"
                        aria-label={`valor ${v.label}`}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </Label>
          </FormCard>

          <FormCard
            title={
              <FormattedMessage
                id="step1.titlePreRequisitos"
                defaultMessage="Pré-requisitos para candidatura"
              />
            }
          >
            <InfoBox
              illustration={<IlustracaoPrancheta />}
              title={
                <FormattedMessage
                  id="step1.infoxTitleRequisitos"
                  defaultMessage="Como funcionam os pré-requisitos!"
                />
              }
              description={
                <FormattedMessage
                  id="step1.infoboxDescriptionRequisitos"
                  defaultMessage="Ao restringi-los, alguns profissionais serão impedidos de se candidatarem a esta vaga. Utilize estes campos com cautela."
                />
              }
            />
            <Space height="1rem" />

            <Space height="1rem" />

            <Select
              label={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.labelEscolaridadeOpcional"
                    defaultMessage={"Escolaridade mínima"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.labelEscolaridadeObrigatoria"
                    defaultMessage={"Escolaridade mínima *"}
                  />
                )
              }
              value={formState.escolaridadeMinimaSelectedId ?? ""}
              onChange={(e) =>
                setFormState({
                  escolaridadeMinimaSelectedId: Number(e.target.value),
                })
              }
              required={!isVagaModelo}
              shouldValidate={hasTriedToSubmit}
              errorMessage={
                <FormattedMessage
                  id="erro.campoObrigatorio"
                  defaultMessage="Campo obrigatório"
                />
              }
              data-testid="select-escolaridade"
            >
              {renderSelectOptions({
                data: formState.escolaridadeMinimaData,
                error: formState.escolaridadeMinimaError,
                errorMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.erro",
                    defaultMessage: "Ocorreu um erro",
                  })
                ),
                loadingMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.loading",
                    defaultMessage: "Carregando...",
                  })
                ),
                selectOptionMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.escolherOpcao",
                    defaultMessage: "Selecione uma opção...",
                  })
                ),
              })}
            </Select>

            <Space height="1rem" />

            <Select
              label={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.nivelHierarquicoOpcional"
                    defaultMessage={"Nível hierárquico"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.nivelHierarquicoObrigatorio"
                    defaultMessage={"Nível hierárquico *"}
                  />
                )
              }
              aria-label="Nível hierárquico"
              placeholder={intl.formatMessage(
                defineMessage({
                  id: "step1.placeholderNivel",
                  defaultMessage:
                    "Qual o nível você precisa? (Júnior, pleno, sênior...)",
                })
              )}
              value={formState.nivelHierarquicoSelectedId ?? ""}
              onChange={(e) => {
                setFormState({
                  nivelHierarquicoSelectedId: Number(e.target.value),
                });
                if (e.target.value === "28")
                  setFormState({ modeloContratacaoSelectedId: 2 });
              }}
              required={!isVagaModelo}
              shouldValidate={hasTriedToSubmit}
              errorMessage={
                <FormattedMessage
                  id="erro.campoObrigatorio"
                  defaultMessage="Campo obrigatório"
                />
              }
              data-testid="input-nivel-hierarquico"
            >
              {renderSelectOptions({
                data: formState.niveisHierarquicosData,
                error: formState.niveisHierarquicosError,
                errorMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.erro",
                    defaultMessage: "Ocorreu um erro",
                  })
                ),
                loadingMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.loading",
                    defaultMessage: "Carregando...",
                  })
                ),
                selectOptionMessage: intl.formatMessage(
                  defineMessage({
                    id: "select.escolherOpcao",
                    defaultMessage: "Selecione uma opção...",
                  })
                ),
              })}
            </Select>

            <Space height="1rem" />

            <Label
              data-testid="multiselect-areas"
              text={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.labelTextAreasOpcional"
                    defaultMessage={"Áreas de atuação"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.labelTextAreasObrigatorio"
                    defaultMessage={"Áreas de atuação *"}
                  />
                )
              }
              details={
                <FormattedMessage
                  id="step1.labelDetaisAreas"
                  defaultMessage="(pesquise e escolha até 8 áreas)"
                />
              }
            >
              <Multi
                name="areas"
                options={
                  formState.areasAtuacaoSelected.length === limiteAreas
                    ? []
                    : formState.areasAtuacaoData
                }
                required={!isVagaModelo}
                state={formState.areasAtuacaoSelected}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.descricao}
                onChange={(v) => {
                  setFormState({ areasAtuacaoSelected: v });
                }}
                noOptionsMessage={() => {
                  return formState.areasAtuacaoSelected.length ===
                    limiteAreas ? (
                    <FormattedMessage
                      id="step1-limiteAreas"
                      defaultMessage="Você atingiu o máximo de áreas"
                    />
                  ) : (
                    <FormattedMessage
                      id="mensagem.ocorreuErro"
                      defaultMessage="Ocorreu um erro"
                    />
                  );
                }}
                placeholder={
                  <Text size="sm" fontColor={vars.color.lightForeground}>
                    <FormattedMessage
                      id="placeholder.pesquiseSelecione"
                      defaultMessage="Pesquise e selecione"
                    />
                  </Text>
                }
                errorMessage={
                  <FormattedMessage
                    id="erro.campoObrigatorio"
                    defaultMessage="Campo obrigatório"
                  />
                }
                shouldValidate={hasTriedToSubmit}
              ></Multi>
            </Label>

            <Space height="1rem" />

            <div className={separatorStyle}></div>

            <Space height="1rem" />

            <Fieldset
              title={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.titleModeloDeTrabalhoOpcional"
                    defaultMessage={"Modelo de trabalho"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.titleModeloDeTrabalhoObrigatorio"
                    defaultMessage={"Modelo de trabalho *"}
                  />
                )
              }
              isValid={formState.localTrabalhoSelectedId}
              shouldValidate={hasTriedToSubmit}
              errorMessage={
                <FormattedMessage
                  id="step1.erroInformeModeloTrabalho"
                  defaultMessage="Campo obrigatório. Selecione um modelo de trabalho."
                />
              }
            >
              <div style={{ display: "flex", direction: "row" }}>
                {!formState.locaisTrabalhoData ? (
                  <Label
                    text={
                      <FormattedMessage
                        id={`${
                          formState.locaisTrabalhoError
                            ? "mensagem.ocorreuErro"
                            : "info.carregando"
                        }`}
                        defaultMessage={`${
                          formState.locaisTrabalhoError
                            ? "Ocorreu um erro"
                            : "Carregando..."
                        }`}
                      />
                    }
                  />
                ) : (
                  formState.locaisTrabalhoData?.map((item) => (
                    <Fragment key={`${item.id}-${item.tipo}`}>
                      <RadioButton
                        label={
                          <FormattedMessage
                            id={`step1.${item.id}-${item.tipo}`}
                            defaultMessage={item.nome}
                          />
                        }
                        id="modelo-de-trabalho"
                        name="modeloTrabalho"
                        value={item.id}
                        checked={formState.localTrabalhoSelectedId === item.id}
                        onChange={(e) =>
                          setFormState({
                            localTrabalhoSelectedId: Number(e.target.value),
                          })
                        }
                        required={!isVagaModelo}
                        data-testid="radio-modelo_trabalho"
                      />
                      <Space />
                    </Fragment>
                  ))
                )}
              </div>
            </Fieldset>

            <Space height="1rem" />

            <Input
              label={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.labelInformeEnderecoOpcional"
                    defaultMessage={"Local de trabalho"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.labelInformeEnderecoObrigatorio"
                    defaultMessage={"Local de trabalho *"}
                  />
                )
              }
              ref={addressFieldRef}
              type="text"
              placeholder={intl.formatMessage(
                defineMessage({
                  id: "step1.placeholderLocal",
                  defaultMessage: "Informe um endereço, cidade ou estado",
                })
              )}
              autoComplete="off"
              required={!isVagaModelo}
              shouldValidate={hasTriedToSubmit}
              errorMessage={
                <FormattedMessage
                  id="step1.erroInformeLocal"
                  defaultMessage="Campo obrigatório. Selecione um local da lista."
                />
              }
              data-testid="input-endereco"
              value={formState.enderecoQuery}
              onChange={(e) => setFormState({ enderecoQuery: e.target.value })}
              validate={(isValidInternally) => {
                return isValidInternally && formState.enderecoFormatado;
              }}
            />

            <Space />

            <Fieldset
              title={
                isVagaModelo ? (
                  <FormattedMessage
                    id="step1.restricaoProfissionaisCandidaturaOpcional"
                    defaultMessage={"Aceitar candidaturas:"}
                  />
                ) : (
                  <FormattedMessage
                    id="step1.restricaoProfissionaisCandidaturaObrigatorio"
                    defaultMessage={"Aceitar candidaturas: *"}
                  />
                )
              }
              isValid={formState.enderecoRestricao || isVagaModelo}
              shouldValidate={hasTriedToSubmit}
              errorMessage={
                <FormattedMessage
                  id="erro.campoObrigatorio"
                  defaultMessage="Campo obrigatório"
                />
              }
            >
              <RadioButton
                label={
                  <FormattedMessage
                    id="step1.radioCidade"
                    defaultMessage="Apenas da cidade {cidade}"
                    values={{
                      cidade: formState.enderecoApiCidade,
                    }}
                  />
                }
                name="enderecoRestricao"
                value="somente_cidade"
                checked={formState.enderecoRestricao === "somente_cidade"}
                onChange={(e) =>
                  setFormState({ enderecoRestricao: e.target.value })
                }
                disabled={!formState.enderecoApiCidade}
                required={!isVagaModelo}
                data-testid="radio-somente_cidade"
              />
              <RadioButton
                label={
                  <FormattedMessage
                    id="step1.radioEstado"
                    defaultMessage="Apenas do estado {estado}"
                    values={{
                      estado: formState.enderecoApiEstado,
                    }}
                  />
                }
                name="enderecoRestricao"
                value="somente_estado"
                checked={formState.enderecoRestricao === "somente_estado"}
                onChange={(e) =>
                  setFormState({ enderecoRestricao: e.target.value })
                }
                disabled={!formState.enderecoApiEstado}
                data-testid="radio-somente_estado"
              />
              <RadioButton
                label={
                  <FormattedMessage
                    id="step1.radioPais"
                    defaultMessage="Apenas do país {pais}"
                    values={{
                      pais: formState.enderecoApiPais,
                    }}
                  />
                }
                name="enderecoRestricao"
                value="somente_pais"
                checked={formState.enderecoRestricao === "somente_pais"}
                onChange={(e) =>
                  setFormState({ enderecoRestricao: e.target.value })
                }
                disabled={!formState.enderecoApiPais}
                data-testid="radio-somente_pais"
              />
              <RadioButton
                label={
                  <FormattedMessage
                    id="step1.radioSemRestricoes"
                    defaultMessage="Sem restrições"
                  />
                }
                name="enderecoRestricao"
                value="todas_localidades"
                checked={formState.enderecoRestricao === "todas_localidades"}
                onChange={(e) =>
                  setFormState({ enderecoRestricao: e.target.value })
                }
                data-testid="radio-sem-restricoes"
              />
            </Fieldset>
          </FormCard>

          <FormCard
            title={
              isVagaModelo ? (
                <FormattedMessage
                  id="step1.titleSobreEmpresaOpcional"
                  defaultMessage={"Sobre a empresa"}
                />
              ) : (
                <FormattedMessage
                  id="step1.titleSobreEmpresaObrigatorio"
                  defaultMessage={"Sobre a empresa *"}
                />
              )
            }
            description={
              <FormattedMessage
                id="step1.descriptionSobreEmpresa"
                defaultMessage="Descreva sua empresa para atrair talentos. Capriche!"
              />
            }
            data-testid="bloco-sobre-empresa"
          >
            <FieldsetSobreEmpresa
              formState={formState}
              setFormState={setFormState}
              isVagaModelo={isVagaModelo}
              hasTriedToSubmit={hasTriedToSubmit}
            />
          </FormCard>

          <div className={formButtonsWrapper}>
            <Button
              className={`${secondaryButton} ${inputVariants["form-buttons"]}`}
              type="button"
              data-testid="btn-voltar"
              onClick={() => history.push("/")}
            >
              <FormattedMessage id="botao.voltar" defaultMessage="Voltar" />
            </Button>
            <Space />
            <Button
              className={`${primaryButton} ${inputVariants["form-buttons"]}`}
              type="submit"
              onClick={() => setHasTriedToSubmit(true)}
              data-testid="btn-continuar"
              disabled={
                (formState.isAnuncioConfidencial === false &&
                  !formState.apresentacaoId) ||
                ((!formState.enderecoFormatado ||
                  !formState.areasAtuacaoSelected?.length ||
                  descricaoTextLength + outrosRequisitosTextLength < 300) &&
                  !isVagaModelo)
              }
              id="continuar-para-step2"
            >
              <FormattedMessage
                id="botao.continuar"
                defaultMessage="Continuar"
              />
            </Button>
          </div>
          <Toaster toasts={toasts} />
        </>
      )}
    </form>
  );
}
